import React, { useState, useEffect, lazy, Suspense } from 'react';
import { 
  Shield, 
  Search, 
  MapPin, 
  Star, 
  Calendar, 
  Clock, 
  Trophy, 
  User, 
  Users,
  ChevronRight,
  Menu,
  X,
  AlertCircle,
  Zap,
  Loader2,
  Inbox,
  Mail,
  Check,
  AlertTriangle,
  LogOut,
  MessageSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NoMatchesState } from './components/EmptyStates';

const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );
    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
        return { default: () => null };
      }
      throw error;
    }
  });

const SearchPage = lazyWithRetry(() => import('./components/SearchPage'));
const PlayerFUTCard = lazyWithRetry(() => import('./components/PlayerFUTCard'));
const TeamFUTCard = lazyWithRetry(() => import('./components/TeamFUTCard'));
const ProfileSetup = lazyWithRetry(() => import('./components/ProfileSetup'));
const MatchRatingModal = lazyWithRetry(() => import('./components/MatchRatingModal'));
const TeamMatchReportModal = lazyWithRetry(() => import('./components/TeamMatchReportModal'));
const MatchChat = lazyWithRetry(() => import('./components/MatchChat'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/LegalContent').then(m => ({ default: m.PrivacyPolicy })));
const TermsConditions = lazyWithRetry(() => import('./components/LegalContent').then(m => ({ default: m.TermsConditions })));
const FAQ = lazyWithRetry(() => import('./components/FAQ'));
const AdminPanel = lazyWithRetry(() => import('./components/AdminPanel'));
import { SkeletonProfile, SkeletonDashboard } from './components/Skeletons';
import { MOCK_DATA } from './data/mockData';
import { supabase } from './lib/supabaseClient';
import heroBg from './assets/hero-bg.webp';
import './styles/globals.css';
// useEffect ya importado arriba
import Navbar from './components/layout/Navbar';
import Hero from './components/layout/Hero';
import Footer from './components/layout/Footer';
const RegistrationModal = lazyWithRetry(() => import('./components/modals/RegistrationModal'));
const BookingSummaryModal = lazyWithRetry(() => import('./components/modals/BookingSummaryModal'));
const NotificationModal = lazyWithRetry(() => import('./components/modals/NotificationModal'));
import ProfileCard from './components/ProfileCard';
const UserDashboard = lazyWithRetry(() => import('./components/UserDashboard'));
import ChatBubble from './components/ChatBubble';
import PullToRefresh from './components/PullToRefresh';
import HallOfFameBanner from './components/HallOfFameBanner';



import { useCitadosData } from './hooks/useCitadosData';
import { usePushNotifications } from './hooks/usePushNotifications';






// Loading fallback
const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
    <Loader2 className="animate-spin" size={48} color="var(--primary)" />
  </div>
);

const App = () => {
  const [view, setView] = useState('landing');
  const [previousView, setPreviousView] = useState('landing');
  const [showRegister, setShowRegister] = useState(false);
  const [bookingProfile, setBookingProfile] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [reportTeamMatchOpen, setReportTeamMatchOpen] = useState(false);
  const [futCardProfile, setFutCardProfile] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [searchTypeFilter, setSearchTypeFilter] = useState('todos');

  const handleGoToRanking = () => {
    setSearchTypeFilter('ranking');
    setView('search');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewCard = (profile) => {
    setPreviousView(view);
    setFutCardProfile(profile);
    setView('fut-card');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const {
    user,
    profiles,
    hallOfFameSnapshots,
    isLoading,
    isAdmin,
    adminStats,
    fetchAdminStats,
    receivedPending,
    sentPending,
    confirmedInvitations,
    pendingNoShowReports,
    recentMatches,
    pendingTeamMatch,
    setPendingTeamMatch,
    activeMatchToRate,
    setActiveMatchToRate,
    showRatingModal,
    setShowRatingModal,
    isConfirmingMatch,
    isSaving,
    setIsSaving,
    editingProfile,
    setEditingProfile,
    fetchProfiles,
    searchByRadius,
    fetchInvitations,
    handleAcceptInvitation,
    handleRejectInvitation,
    handleConfirmTeamMatch,
    handleDisputeTeamMatch,
    handleDisputeNoShow,
    handleConfirmNoShow,
    handleSaveRating,
    handleProfileComplete,
    handleToggleAvailability,
    handleToggleProfileField,
    handleDeleteProfile,
    handleDevLogin,
    internalNotifications,
    unreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    handleResolveDispute,
    disputes,
    openSOSAlerts,
    handleCreateSOSAlert,
    handleCancelSOSAlert,
    handleAcceptSOSAlert
  } = useCitadosData({ 
    view, 
    setNotification, 
    setShowRegister, 
    setView,
    setShowPrivacy, 
    setShowTerms    
  });

  const { isSubscribed, subscribeUser, unsubscribeUser, permission } = usePushNotifications(user);

  return (
    <div className="app">
      <PullToRefresh />

      <AnimatePresence>
        <Suspense fallback={null}>
        {showRegister && (
          <RegistrationModal 
            onClose={() => setShowRegister(false)} 
            onShowPrivacy={() => setShowPrivacy(true)}
            onShowTerms={() => setShowTerms(true)}
          />
        )}
        {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
        {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}
        {bookingProfile && (
          <BookingSummaryModal 
            profile={bookingProfile} 
            onClose={() => setBookingProfile(null)} 
            user={user} 
            hostCredits={profiles.filter(p => p.userId === user?.id).reduce((acc, curr) => acc + (curr.credits || 0), 0)} 
            onCreateSOSAlert={handleCreateSOSAlert}
            onCreateProfile={(cat) => {
              setBookingProfile(null);
              setEditingProfile(null);
              setView(`setup-${cat}`);
            }}
          />
        )}
        </Suspense>
      </AnimatePresence>

      {/* GSAP-driven overlay — handles its own enter/exit animations */}
      <Suspense fallback={null}>
        {showRatingModal && activeMatchToRate && (
          <MatchRatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            match={activeMatchToRate}
            players={activeMatchToRate.players}
            onSave={handleSaveRating}
          />
        )}
      </Suspense>

      {/* Team Match Report Modal */}
      <Suspense fallback={null}>
        {user && profiles.find(p => p.userId === user.id && p.type === 'equipo') && (
        <TeamMatchReportModal
          isOpen={reportTeamMatchOpen}
          loading={isSaving}
          onClose={() => setReportTeamMatchOpen(false)}
          myTeam={profiles.find(p => p.userId === user.id && p.type === 'equipo')}
          allProfiles={profiles}
          onSave={async (reportData) => {
            setIsSaving(true);
            try {
              const { error } = await supabase
                .from('citados_team_matches')
                .insert({
                  reporter_team_id: reportData.reporterTeamId,
                  opponent_team_id: reportData.opponentTeamId,
                  reporter_goals: reportData.reporterGoals,
                  opponent_goals: reportData.opponentGoals,
                  status: 'esperando_confirmacion'
                });
                
              if (error) throw error;
              
              setNotification({ type: 'success', message: 'Reporte enviado correctamente' });
              // We DON'T close the modal here because the modal shows its own success screen (Step 2)
            } catch (err) {
              console.error('Error al guardar reporte de equipo:', err);
              setNotification({ type: 'error', message: 'Error al enviar el reporte' });
              // Close on error so user isn't stuck in a broken state
              setReportTeamMatchOpen(false);
              throw err; // Re-throw for modal catch
            } finally {
              setIsSaving(false);
            }
          }}
        />
        )}
      </Suspense>

      <Navbar 
        user={user}
        isAdmin={isAdmin}
        pushNotifications={{ isSubscribed, permission }}
        onSearch={() => user ? setView('search') : setShowRegister(true)} 

        onLogo={(v) => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          if (v === 'cards' && user) {
            const myProfiles = profiles.filter(p => p.userId === user.id);
            const playerP = myProfiles.find(p => p.type === 'jugador');
            const teamP = myProfiles.find(p => p.type === 'equipo');
            
            if (playerP) {
              handleViewCard(playerP);
            } else if (teamP) {
              handleViewCard(teamP);
            } else {
              setView('profile');
            }
            return;
          }
          setView(typeof v === 'string' ? v : 'landing');

        }} 
        onLogin={() => setShowRegister(true)}
        onShowFAQ={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setView('faq');
        }}
        onAdmin={() => setShowAdminPanel(true)}
        notifications={internalNotifications}
        unreadCount={unreadCount}
        onMarkAsRead={markNotificationAsRead}
        onMarkAllAsRead={markAllNotificationsAsRead}
        onNavigate={(url) => {
          if (url === '/profile' || url === '/dashboard') {
            setView('profile');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (url === '/') {
            setView('landing');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (url.startsWith('/')) {
             setView(url.substring(1));
             window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
      />
      <main>
        <AnimatePresence mode="wait">
          <Suspense fallback={<PageLoader />}>
          {view === 'landing' ? (
            <motion.div 
              key="landing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Hero onStart={() => user ? setView('search') : setShowRegister(true)} onStartAtajar={() => setShowRegister(true)} />
              
              {/* Hall of Fame Banner en pantalla de inicio */}
              <div className="container" style={{ marginTop: '3rem', marginBottom: '1rem' }}>
                <HallOfFameBanner 
                  profiles={profiles}
                  hallOfFameSnapshots={hallOfFameSnapshots}
                  onViewCard={handleViewCard}
                  onGoToRanking={handleGoToRanking}
                />
              </div>

              <section style={{ padding: '6rem 0', background: 'radial-gradient(circle at top right, rgba(212, 255, 0, 0.05), transparent 40%)' }}>
                <div className="container">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '3rem' }}>
                    <h2 style={{ fontSize: '2rem' }}>Destacados de la zona</h2>
                    <p style={{ color: 'var(--text-muted)' }}>Jugadores y equipos listos para el picado.</p>
                  </div>
                  <div className="responsive-grid">
                    {isLoading ? (
                      [...Array(6)].map((_, i) => <SkeletonProfile key={i} />)
                    ) : (
                      <>
                        {profiles.map(gk => (
                          <ProfileCard 
                            key={gk.id} 
                            gk={gk} 
                            onBook={(p) => user ? setBookingProfile(p) : setShowRegister(true)} 
                            onViewCard={handleViewCard} 
                            currentUser={user}
                          />
                        ))}
                        {!user && (
                          <motion.div 
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            style={{ 
                              gridColumn: '1 / -1', 
                              textAlign: 'center', 
                              padding: '3rem', 
                              marginTop: '2rem',
                              background: 'rgba(255,255,255,0.02)',
                              borderRadius: '2rem',
                              border: '1px dashed rgba(255,255,255,0.1)'
                            }}
                          >
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>¿Querés ver más jugadores?</h3>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>Hay cientos de jugadores en tu zona esperando ser citados.</p>
                            <button className="btn-primary" onClick={() => setShowRegister(true)}>Registrate gratis</button>
                          </motion.div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </section>
              <section style={{ padding: '6rem 0' }}>
                <div className="container">
                  <h2 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '4rem' }}>¿Cómo funciona?</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))', gap: '2rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div className="glass" style={{ width: '64px', height: '64px', borderRadius: '20px', display: 'grid', placeItems: 'center', margin: '0 auto 1.5rem' }}>
                        <Search size={28} color="var(--primary)" />
                      </div>
                      <h3>1. Buscá</h3>
                      <p style={{ color: 'var(--text-muted)', marginTop: '1rem' }}>Filtrá por zona, tipo de cancha y horario del partido.</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div className="glass" style={{ width: '64px', height: '64px', borderRadius: '20px', display: 'grid', placeItems: 'center', margin: '0 auto 1.5rem' }}>
                        <User size={28} color="var(--primary)" />
                      </div>
                      <h3>2. Elegí</h3>
                      <p style={{ color: 'var(--text-muted)', marginTop: '1rem' }}>Revisá su reputación y estadísticas antes de confirmar.</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div className="glass" style={{ width: '64px', height: '64px', borderRadius: '20px', display: 'grid', placeItems: 'center', margin: '0 auto 1.5rem' }}>
                        <Calendar size={28} color="var(--primary)" />
                      </div>
                      <h3>3. Reservá</h3>
                      <p style={{ color: 'var(--text-muted)', marginTop: '1rem' }}>Confirmá y pagá. Todo listo para que empiece el partido.</p>
                    </div>
                  </div>
                </div>
              </section>
              <FAQ />
            </motion.div>
          ) : view === 'search' ? (
            <motion.div 
              key="search"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <SearchPage 
                profiles={profiles} 
                searchByRadius={searchByRadius}
                onBook={(p) => user ? setBookingProfile(p) : setShowRegister(true)} 
                onViewCard={handleViewCard} 
                currentUser={user}
                initialTypeFilter={searchTypeFilter}
                hallOfFameSnapshots={hallOfFameSnapshots}
              />
              <FAQ />
            </motion.div>
          ) : view === 'profile' ? (
            <motion.div 
              key="profile"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              {!user ? (
                <div style={{ textAlign: 'center', padding: '10rem 0' }}>
                   <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Iniciá sesión para ver tu perfil</h2>
                   <button className="btn-primary" onClick={() => setShowRegister(true)}>Ingresar con Google</button>
                </div>
              ) : (
                isLoading ? (
                  <SkeletonDashboard />
                ) : (
                  <UserDashboard 
                    user={user} 
                    profiles={profiles} 
                    hallOfFameSnapshots={hallOfFameSnapshots}
                    onGoToRanking={handleGoToRanking} 
                    onCreateProfile={(cat) => {
                      setEditingProfile(null);
                      setView(`setup-${cat}`);
                    }}
                    onEdit={(profile) => {
                      setEditingProfile({ ...profile, name: profile.fullName || profile.name });
                      setView(`setup-${profile.type}`);
                    }}
                    onViewCard={handleViewCard}
                    onLogout={() => supabase.auth.signOut()}
                    pendingTeamMatch={pendingTeamMatch}
                    onConfirmMatch={handleConfirmTeamMatch}
                    onDisputeMatch={handleDisputeTeamMatch}
                    onReportMatch={() => setReportTeamMatchOpen(true)}
                    onReviewMatch={(invit) => {
                      const players = [];
                      const targetProfileId = invit.role === 'host' ? invit.player_id : invit.host_profile_id;
                      const p = profiles.find(profile => profile.id === targetProfileId);
                      
                      if (p) {
                        players.push({
                          id: p.id,
                          userId: p.userId,
                          bookingId: invit.id,
                          name: p.name,
                          image: p.image || `https://api.dicebear.com/7.x/initials/svg?seed=${p.name}`,
                          position: p.position,
                          gender: p.gender
                        });
                      }

                      setActiveMatchToRate({
                        id: invit.id,
                        date: `${new Date(invit.scheduled_at).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(invit.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}hs`,
                        location: invit.pitch_name || 'Cancha por confirmar',
                        bookingIds: [invit.id],
                        players: players,
                        isHost: invit.role === 'host'
                      });
                      setShowRatingModal(true);
                    }}
                    recentMatches={recentMatches}
                    invitations={receivedPending}
                    sentInvitations={sentPending}
                    confirmedInvitations={confirmedInvitations}
                    onAcceptInvitation={handleAcceptInvitation}
                    onRejectInvitation={handleRejectInvitation}
                    pendingNoShowReports={pendingNoShowReports}
                    onConfirmNoShow={handleConfirmNoShow}
                    onDisputeNoShow={handleDisputeNoShow}
                    pushNotifications={{ isSubscribed, subscribeUser, unsubscribeUser, permission }}
                    onOpenChat={setActiveChat}
                    openSOSAlerts={openSOSAlerts}
                    onCreateSOSAlert={handleCreateSOSAlert}
                    onCancelSOSAlert={handleCancelSOSAlert}
                    onAcceptSOSAlert={handleAcceptSOSAlert}
                    onDeleteProfile={handleDeleteProfile}
                  />
                )
              )}
            </motion.div>
          ) : view.startsWith('setup-') ? (
            <motion.div 
              key="setup"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <ProfileSetup 
                loading={isSaving}
                initialCategory={view.split('-')[1]} 
                initialData={editingProfile}
                onComplete={handleProfileComplete}
                onToggleAvailability={handleToggleAvailability}
                onToggleField={handleToggleProfileField}
              />
            </motion.div>
          ) : (view === 'fut-card' && futCardProfile) ? (() => {
            const myProfiles = user ? profiles.filter(p => p.userId === user.id) : [];
            const playerP = myProfiles.find(p => p.type === 'jugador');
            const teamP = myProfiles.find(p => p.type === 'equipo');
            const isOwnCard = user && futCardProfile.userId === user.id;
            const hasBoth = playerP && teamP;

            return (
              <motion.div
                key="fut-card"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  minHeight: '80vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem 1rem',
                  background: 'radial-gradient(circle at 50% 30%, rgba(212, 255, 0, 0.03), transparent 60%)'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1.5rem', 
                  marginBottom: '2rem',
                  width: '100%',
                  maxWidth: '400px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <button
                      onClick={() => setView(previousView)}
                      style={{ 
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        padding: '0.6rem 1.2rem',
                        borderRadius: '0.8rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      ← Volver
                    </button>
                    
                    {isOwnCard ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary)' }}>
                        <Trophy size={18} />
                        <span style={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '1px' }}>MIS CARTAS</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => user ? setBookingProfile(futCardProfile) : setShowRegister(true)}
                        className="btn-primary"
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1.5rem', boxShadow: '0 4px 15px rgba(205, 255, 0, 0.2)' }}
                      >
                        Coordinar
                      </button>
                    )}
                  </div>

                  {isOwnCard && hasBoth && (
                    <div style={{ 
                      display: 'flex', 
                      background: 'rgba(255,255,255,0.05)', 
                      padding: '0.3rem', 
                      borderRadius: '99rem',
                      border: '1px solid rgba(255,255,255,0.1)',
                      width: 'fit-content'
                    }}>
                      <button 
                        onClick={() => setFutCardProfile(playerP)}
                        style={{
                          padding: '0.5rem 1.2rem',
                          borderRadius: '99rem',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          transition: 'all 0.3s',
                          background: futCardProfile.type === 'jugador' ? 'var(--primary)' : 'transparent',
                          color: futCardProfile.type === 'jugador' ? '#000' : 'rgba(255,255,255,0.5)'
                        }}
                      >
                        Player
                      </button>
                      <button 
                        onClick={() => setFutCardProfile(teamP)}
                        style={{
                          padding: '0.5rem 1.2rem',
                          borderRadius: '99rem',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          transition: 'all 0.3s',
                          background: futCardProfile.type === 'equipo' ? 'var(--primary)' : 'transparent',
                          color: futCardProfile.type === 'equipo' ? '#000' : 'rgba(255,255,255,0.5)'
                        }}
                      >
                        Equipo
                      </button>
                    </div>
                  )}
                </div>

                {futCardProfile.type === 'equipo' ? (
                  <TeamFUTCard
                    team={{
                      name: futCardProfile.name,
                      location: futCardProfile.location,
                      image: futCardProfile.image,
                      xp: futCardProfile.xp || 1200,
                      category: futCardProfile.category
                    }}
                    stats={{
                      matches: futCardProfile.team_matches_played || 0,
                      wins: futCardProfile.team_wins || 0,
                      goalsScored: futCardProfile.team_goals_scored || 0,
                      goalDifference: (futCardProfile.team_goals_scored || 0) - (futCardProfile.team_goals_conceded || 0),
                      reputation: futCardProfile.team_reputation || 100
                    }}
                    isOwn={isOwnCard}
                    onBook={() => setBookingProfile(futCardProfile)}
                  />
                ) : (
                  <PlayerFUTCard
                    profile={futCardProfile}
                    stats={futCardProfile.stats}
                    isOwn={isOwnCard}
                    onBook={() => user ? setBookingProfile(futCardProfile) : setShowRegister(true)}
                  />
                )}
              </motion.div>
            );
          })() : view === 'faq' ? (
            <motion.div
              key="faq"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <FAQ />
              <div style={{ textAlign: 'center', paddingBottom: '4rem' }}>
                <button onClick={() => setView('landing')} className="btn-outline">← Volver al inicio</button>
              </div>
            </motion.div>
          ) : null}
          </Suspense>
        </AnimatePresence>
      </main>

      <Footer 
        onShowPrivacy={() => setShowPrivacy(true)} 
        onShowTerms={() => setShowTerms(true)} 
        onSearch={() => user ? setView('search') : setShowRegister(true)} 
        onShowFAQ={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setView('faq');
        }}
      />
      <Suspense fallback={null}>
        {notification && (
        <NotificationModal 
          type={notification.type} 
          message={notification.message}
          data={notification.data}
          onClose={() => setNotification(null)} 
          onAction={(action) => {
            if (action === 'view_match') {
              setView('profile');
              setNotification(null);
              // Wait for view transition then scroll to the pending banner
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 100);
            }
          }}
        />
        )}
      </Suspense>


      <AnimatePresence>
        {showAdminPanel && isAdmin && (
          <Suspense fallback={null}>
            <AdminPanel 
              stats={{ ...adminStats, disputes }} 
              onFetchStats={fetchAdminStats} 
              onResolveDispute={handleResolveDispute}
              onClose={() => setShowAdminPanel(false)} 
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Dev Auth Bypass Buttons */}
      {import.meta.env.DEV && !user && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {/* ADMIN DEV */}
          <button
            onClick={() => handleDevLogin('00000000-0000-0000-0000-000000000000', 'Super Admin', 'admin@citados.com.ar')}
            style={{ 
              padding: '10px 15px', 
              fontSize: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
            }}
          >
            <Shield size={16} />
            LOGIN ADMIN
          </button>
          {/* EZE PALMERI - user real que existe en auth.users y tiene bookings en la DB */}
          <button
            onClick={() => handleDevLogin('3441f918-424a-4387-aec5-0460bbcb14cc', 'Eze Palmeri', 'ezepalmeri@gmail.com')}
            className="btn-primary"
            style={{ padding: '10px 15px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Shield size={16} />
            LOGIN EZE (Host)
          </button>
          {/* MATIAS NORVERT - user real que existe en auth.users y tiene booking pendiente */}
          <button
            onClick={() => handleDevLogin('e2683a33-b7f2-4ce7-b300-ae7fa201d5e5', 'Matias Norvert', 'matias@example.com')}
            style={{ 
              padding: '10px 15px', 
              fontSize: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              background: '#fff',
              color: '#000',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            <User size={16} />
            LOGIN MATIAS (Acepta)
          </button>
        </div>
      )}

      <Suspense fallback={null}>
        <AnimatePresence>
          {activeChat && (
            <MatchChat 
              booking={activeChat}
              user={user}
              onClose={() => setActiveChat(null)}
            />
          )}
        </AnimatePresence>
      </Suspense>

      <ChatBubble 
        activeChats={confirmedInvitations} 
        user={user} 
        onOpenChat={setActiveChat} 
      />
    </div>
  );
};

export default App;
